import type { RowData } from '@tanstack/react-table';
import { ChevronDownIcon, XIcon } from 'lucide-react';
import * as React from 'react';

import { useTranslation } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@nucleus-commerce/ui/components/dropdown-menu';
import { Spinner } from '@nucleus-commerce/ui/components/spinner';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import type { BulkAction } from './types';
import type { DataTableInstance } from './use-data-table';

export interface DataTableBulkActionsProps<TData extends RowData> {
  table: DataTableInstance<TData>;
  actions: ReadonlyArray<BulkAction<TData>>;
  /**
   * Offer "Select all N" across pages. Default true; shown only when a total
   * says there are more rows than the page shows. In cursor-with-total mode
   * only when at least one action `supportsAllMatching`.
   */
  selectAllMatching?: boolean;
  /**
   * The most rows "Select all N" may cover (the bulk endpoint's limit). Above
   * it, or when the total is capped, the bar asks to narrow the filter instead.
   */
  allMatchingMax?: number;
  /** Translated nouns for the copy: "products". */
  resourceName: { singular: string; plural: string };
  /** Classes for the header cells, so the bar keeps the header's height and stickiness. */
  cellClassName?: string;
  selectCellClassName?: string;
  columnCount: number;
}

/**
 * Replaces the header row while rows are selected (Polaris IndexTable).
 * Promoted actions are buttons; the rest sit under "More actions" (or
 * "Actions" when none is promoted).
 */
function DataTableBulkActions<TData extends RowData>({
  table,
  actions,
  selectAllMatching = true,
  allMatchingMax,
  resourceName,
  cellClassName,
  selectCellClassName,
  columnCount,
}: DataTableBulkActionsProps<TData>) {
  const { t, i18n } = useTranslation('common');
  const { selection } = table;
  const [pending, setPending] = React.useState<string | null>(null);
  const promoted = actions.filter((action) => action.promoted);
  const overflow = actions.filter((action) => !action.promoted);
  const allMatching = selection.selection.type === 'exclude';
  // Without a total (cursor mode) there is no "Select all N": only loaded rows are known.
  const moreRows = table.rowCount !== undefined && table.rowCount > table.rows.length;
  // Cursor with total: only actions that resolve the query can use "all matching".
  const offersAllMatching =
    selectAllMatching &&
    moreRows &&
    (table.cursorPaging === undefined || actions.some((action) => action.supportsAllMatching));
  const overLimit =
    allMatchingMax !== undefined &&
    (table.rowCountCapped || (table.rowCount ?? 0) > allMatchingMax);
  const count = selection.selectedCount;
  const noun = count === 1 ? resourceName.singular : resourceName.plural;

  const isDisabled = (action: BulkAction<TData>) =>
    pending !== null || action.disabled === true || (allMatching && !action.supportsAllMatching);

  const run = async (action: BulkAction<TData>) => {
    setPending(action.id);
    try {
      const rows = table.rows
        .filter((row) => selection.isRowSelected(row.id))
        .map((row) => row.original);
      await action.onAction(selection.selection, rows);
      if (!action.keepSelection) selection.clear();
    } finally {
      setPending(null);
    }
  };

  const status = selection.isAllMatchingSelected
    ? t('indexTable.selection.allSelected', {
        count: table.rowCount ?? count,
        resource: resourceName.plural,
      })
    : t('table.selected', { count });

  return (
    <tr data-slot="data-table-bulk-actions">
      <th scope="col" className={selectCellClassName}>
        <Checkbox
          checked={
            selection.isAllPageRowsSelected || (selection.isSomePageRowsSelected && 'indeterminate')
          }
          onCheckedChange={(checked) => selection.togglePage(checked === true)}
          aria-label={t(
            selection.isAllPageRowsSelected
              ? 'indexTable.selection.deselectPage'
              : 'indexTable.selection.selectPage',
            { resource: resourceName.plural },
          )}
          className="relative after:absolute after:-inset-1"
        />
      </th>
      <th
        scope="col"
        colSpan={Math.max(columnCount - 1, 1)}
        className={cn(cellClassName, 'font-normal')}
      >
        <div
          role="toolbar"
          aria-label={t('indexTable.bulkActions.label')}
          className="sticky left-13 flex max-w-[calc(var(--fujin-data-table-width,100%)-3.25rem)] flex-wrap items-center gap-2 py-1"
        >
          <span role="status" aria-live="polite" className="font-medium whitespace-nowrap">
            {status}
          </span>
          {offersAllMatching && (allMatchingMax !== undefined || !table.rowCountCapped) ? (
            allMatching ? (
              <Button variant="link" size="sm" className="h-7 px-1" onClick={selection.clear}>
                {t('indexTable.bulkActions.undo')}
              </Button>
            ) : selection.isAllPageRowsSelected && overLimit ? (
              <span className="text-muted-foreground whitespace-nowrap">
                {t('indexTable.selection.narrowFilter', {
                  limit: new Intl.NumberFormat(i18n.language).format(allMatchingMax),
                  resource: resourceName.plural,
                })}
              </span>
            ) : selection.isAllPageRowsSelected ? (
              <Button
                variant="link"
                size="sm"
                className="h-7 px-1"
                onClick={selection.selectAllMatching}
              >
                {t('indexTable.selection.selectAllMatching', {
                  count: table.rowCount,
                  resource: resourceName.plural,
                })}
              </Button>
            ) : null
          ) : null}
          {promoted.map((action) => (
            <Button
              key={action.id}
              variant={action.variant === 'destructive' ? 'destructive' : 'outline'}
              size="sm"
              className="h-7"
              disabled={isDisabled(action)}
              aria-busy={pending === action.id || undefined}
              title={
                allMatching && !action.supportsAllMatching
                  ? t('indexTable.bulkActions.notForAllMatching', {
                      resource: resourceName.plural,
                    })
                  : undefined
              }
              onClick={() => void run(action)}
            >
              {pending === action.id ? <Spinner label={null} /> : action.icon}
              {action.label}
            </Button>
          ))}
          {overflow.length > 0 ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-7" disabled={pending !== null}>
                  {promoted.length > 0 ? t('indexTable.bulkActions.more') : t('indexTable.actions')}
                  <ChevronDownIcon aria-hidden />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {overflow.map((action) => (
                  <DropdownMenuItem
                    key={action.id}
                    variant={action.variant}
                    disabled={isDisabled(action)}
                    onClick={() => void run(action)}
                  >
                    {action.icon}
                    {allMatching && !action.supportsAllMatching ? (
                      // A disabled item gets no hover, so the reason sits under the label.
                      <span className="flex flex-col">
                        {action.label}
                        <span className="text-muted-foreground text-xs">
                          {t('indexTable.bulkActions.notForAllMatching', {
                            resource: resourceName.plural,
                          })}
                        </span>
                      </span>
                    ) : (
                      action.label
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
          <Button
            variant="ghost"
            size="icon-sm"
            className="ml-auto size-7 shrink-0"
            aria-label={t('indexTable.selection.clear', { count, resource: noun })}
            onClick={selection.clear}
          >
            <XIcon aria-hidden />
          </Button>
        </div>
      </th>
    </tr>
  );
}

export { DataTableBulkActions };
